import React from "react";
import {
  CheckCircle2, AlertTriangle, Package, Truck, Mail, Shield, Database,
  Activity, MessageCircle, RefreshCw, Loader2, Sun, Moon, Cloud, DollarSign,
  ArrowRight, Printer, Zap,
} from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../lib/api";
import { api } from "../lib/api";
import OpenForBusinessWizard from "./OpenForBusinessWizard";
import ManualBookingModal from "./ManualBookingModal";

const REFRESH_MS = 90_000;

function greetingFor(date) {
  const h = date.getHours();
  if (h < 6)  return { text: "God nat",     icon: <Moon size={18} className="text-indigo-400" /> };
  if (h < 11) return { text: "God morgen",  icon: <Sun size={18} className="text-amber-500" /> };
  if (h < 17) return { text: "God dag",     icon: <Sun size={18} className="text-amber-600" /> };
  if (h < 22) return { text: "God aften",   icon: <Cloud size={18} className="text-orange-500" /> };
  return       { text: "God nat",            icon: <Moon size={18} className="text-indigo-400" /> };
}

function todayDanish() {
  return new Date().toLocaleDateString("da-DK", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });
}

export default function MorningCheckPanel({ onJumpToTab }) {
  const [data, setData] = React.useState(null);
  const [availability, setAvailability] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [adminName, setAdminName] = React.useState("");
  const [wizardOpen, setWizardOpen] = React.useState(false);
  const [manualOpen, setManualOpen] = React.useState(false);

  const load = React.useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [r, av] = await Promise.all([
        adminApi().get("/admin/dashboard/today"),
        api.get("/availability").catch(() => ({ data: null })),
      ]);
      setData(r.data);
      setAvailability(av.data);
    } catch (e) {
      if (!silent) toast.error("Kunne ikke hente dagens status");
    } finally { setLoading(false); }
  }, []);

  React.useEffect(() => {
    load();
    const t = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  React.useEffect(() => {
    try {
      // Best-effort: derive admin first name from JWT email
      const tok = localStorage.getItem("ema_admin_token");
      if (tok) {
        const payload = JSON.parse(atob(tok.split(".")[1] || ""));
        const email = payload?.email || "";
        if (email) {
          const local = email.split("@")[0].toLowerCase();
          // Default to Firat for the admin/firat accounts (this tool is built for him)
          const knownNames = { admin: "Firat", firat: "Firat" };
          setAdminName(knownNames[local] || local.charAt(0).toUpperCase() + local.slice(1));
        }
      }
    } catch { /* ignore */ }
  }, []);

  if (loading && !data) {
    return (
      <div className="bg-white border border-brand p-12 text-center text-brand-text2" data-testid="morning-loading">
        <Loader2 size={20} className="animate-spin mx-auto mb-3 text-brand-red" />
        Indlæser dagens status…
      </div>
    );
  }
  if (!data) return null;

  const g = greetingFor(new Date());
  const alerts = data.alerts || [];
  const critical = alerts.filter((a) => a.level === "critical");
  const warnings = alerts.filter((a) => a.level === "warning");
  const allOk = critical.length === 0 && warnings.length === 0;

  // Build comprehensive checklist
  const checks = [
    {
      key: "bookings",
      icon: <Package size={16} />,
      label: "Bookinger i dag",
      value: `${data.bookings.new_today} nye · ${data.bookings.to_pickup} til afhentning · ${data.bookings.in_transit} undervejs`,
      detail: data.bookings.delivered_today > 0 ? `${data.bookings.delivered_today} leveret 🎯` : null,
      ok: data.bookings.problematic === 0,
      problem: data.bookings.problematic > 0 ? `${data.bookings.problematic} problematiske` : null,
      jumpTo: "bookings",
    },
    {
      key: "drivers",
      icon: <Truck size={16} />,
      label: "Chauffører",
      value: `${data.drivers.online} online · ${data.drivers.active_routes} aktive ruter`,
      detail: data.drivers.offline > 0 ? `${data.drivers.offline} offline` : null,
      ok: (data.drivers.paused || 0) === 0,
      problem: data.drivers.paused > 0 ? `${data.drivers.paused} pauset` : null,
      jumpTo: "driver_problems",
    },
    {
      key: "driver_ops",
      icon: <Activity size={16} />,
      label: "Chauffør-problemer",
      value: data.driver_ops?.open_problems > 0
        ? `${data.driver_ops.open_problems} åbne · ${data.driver_ops.in_progress_problems} i gang`
        : "Ingen åbne problemer",
      ok: (data.driver_ops?.severe_open_problems || 0) === 0,
      problem: data.driver_ops?.severe_open_problems > 0 ? `${data.driver_ops.severe_open_problems} AKUTTE` : null,
      jumpTo: "driver_problems",
    },
    {
      key: "messages",
      icon: <MessageCircle size={16} />,
      label: "Chauffør-beskeder",
      value: data.driver_ops?.unread_driver_messages > 0
        ? `${data.driver_ops.unread_driver_messages} ulæste`
        : "Alle besvaret",
      ok: (data.driver_ops?.unread_driver_messages || 0) === 0,
      jumpTo: "driver_chat",
    },
    {
      key: "tickets",
      icon: <Mail size={16} />,
      label: "Tickets / kundeservice",
      value: `${data.tickets?.new || 0} nye · ${data.tickets?.in_progress || 0} under behandling`,
      detail: data.tickets?.complaints > 0 ? `${data.tickets.complaints} klager` : null,
      ok: (data.tickets?.overdue_count || 0) === 0,
      problem: data.tickets?.overdue_count > 0 ? `${data.tickets.overdue_count} over SLA` : null,
      jumpTo: "tickets",
    },
    {
      key: "billy",
      icon: <DollarSign size={16} />,
      label: "Billy-bogføring",
      value: `${data.billy.pending} pending`,
      ok: data.billy.failed_count === 0,
      problem: data.billy.failed_count > 0 ? `${data.billy.failed_count} fejlede pushes` : null,
      jumpTo: "billy",
    },
    {
      key: "system",
      icon: <Mail size={16} />,
      label: "Mail-system (24t)",
      value: (data.system_errors?.smtp_failures?.length || 0) === 0 ? "Ingen fejl" : `${data.system_errors.smtp_failures.length} SMTP-fejl`,
      ok: (data.system_errors?.smtp_failures?.length || 0) === 0,
      jumpTo: "drift",
    },
    {
      key: "backup",
      icon: <Database size={16} />,
      label: "DB-backup (24t)",
      value: (data.system_errors?.backup_failures?.length || 0) === 0 ? "OK" : "Fejlede",
      ok: (data.system_errors?.backup_failures?.length || 0) === 0,
      problem: data.system_errors?.backup_failures?.length > 0 ? "Backup fejlede" : null,
    },
    {
      key: "whistleblower",
      icon: <Shield size={16} />,
      label: "Whistleblower",
      value: data.whistleblower.new > 0
        ? `${data.whistleblower.new} ny${data.whistleblower.new > 1 ? "e" : ""}`
        : `${data.whistleblower.open} åbne`,
      ok: data.whistleblower.new === 0,
      problem: data.whistleblower.new > 0 ? `${data.whistleblower.new} kræver gennemsyn` : null,
      jumpTo: "whistleblower",
    },
    {
      key: "legal",
      icon: <Shield size={16} />,
      label: "Lovsider (årlig revision)",
      value: data.legal_reviews
        ? (data.legal_reviews.overdue > 0
            ? `${data.legal_reviews.overdue} forældede`
            : data.legal_reviews.warning > 0
            ? `${data.legal_reviews.warning} due snart`
            : `Alle ${data.legal_reviews.total} OK`)
        : "—",
      ok: !data.legal_reviews || data.legal_reviews.overdue === 0,
      warn: data.legal_reviews?.overdue === 0 && (data.legal_reviews?.warning || 0) > 0,
      problem: data.legal_reviews?.overdue > 0
        ? `${data.legal_reviews.overdue} > 12 mdr siden review`
        : null,
      warning: data.legal_reviews?.overdue === 0 && (data.legal_reviews?.warning || 0) > 0
        ? `${data.legal_reviews.warning} forfalder inden 30 dage`
        : null,
      jumpTo: "legal",
    },
  ];

  const okCount = checks.filter((c) => c.ok && !c.warn).length;
  const warnCount = checks.filter((c) => c.ok && c.warn).length;
  const problemCount = checks.filter((c) => !c.ok).length;

  return (
    <div className="space-y-5 morning-check-printable" data-testid="morning-check">
      {/* Print-only header — appears at top of PDF */}
      <div className="hidden print:block mb-4 pb-3 border-b border-gray-300">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-gray-500">EMA Kurer ApS · Morgentjek</div>
            <div className="text-2xl font-bold text-black mt-1">Dagens status — {adminName || "Firat"}</div>
            <div className="text-sm text-gray-600 mt-0.5 capitalize">{todayDanish()}</div>
          </div>
          <div className="text-xs text-gray-500">
            Genereret {new Date().toLocaleString("da-DK", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
          </div>
        </div>
      </div>

      {/* Print-friendly CSS — hides everything except this panel when printing */}
      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          body { background: white !important; }
          /* Hide everything */
          body * { visibility: hidden !important; }
          /* Show the morning panel and its children */
          .morning-check-printable, .morning-check-printable * { visibility: visible !important; }
          .morning-check-printable {
            position: absolute !important;
            left: 0; top: 0; width: 100%;
            color: #000 !important;
            background: white !important;
          }
          /* Hide buttons + jump links inside the panel */
          .morning-check-printable button { display: none !important; }
          /* Strip dark hero gradient */
          .morning-check-printable [data-testid="morning-greeting"],
          .morning-check-printable .bg-gradient-to-br {
            background: white !important;
            color: black !important;
            border-left: 4px solid #DC2626 !important;
          }
          .morning-check-printable .bg-gradient-to-br * { color: #1f2937 !important; }
          .morning-check-printable [data-testid="status-all-ok"] {
            background: #ECFDF5 !important; border: 1px solid #10B981 !important;
          }
          .morning-check-printable [data-testid="status-all-ok"] * { color: #065F46 !important; }
          .morning-check-printable [data-testid="status-needs-action"] {
            background: #FEF2F2 !important; border: 1px solid #DC2626 !important;
          }
          .morning-check-printable [data-testid="status-needs-action"] * { color: #991B1B !important; }
          /* Hide quick actions section in print */
          .morning-check-printable .quick-actions-print-hide { display: none !important; }
        }
      `}</style>
      {/* Greeting hero */}
      <div className="bg-gradient-to-br from-brand-dark to-[#1a2540] text-white p-6 sm:p-8 border-l-4 border-brand-red">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-amber-300 text-sm font-bold uppercase tracking-widest">
              {g.icon} {g.text}
            </div>
            <h1 className="font-display font-bold text-3xl sm:text-4xl mt-2" data-testid="morning-greeting">
              {g.text}, {adminName || "Firat"}.
            </h1>
            <p className="text-white/70 text-sm mt-2 capitalize">{todayDanish()}</p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={() => window.print()}
              className="border border-white/20 hover:border-white text-white px-4 py-2 text-sm flex items-center gap-2 transition-colors"
              data-testid="morning-print"
              title="Udskriv eller gem som PDF"
            >
              <Printer size={12} />
              Udskriv / PDF
            </button>
            <button
              onClick={() => load(false)}
              className="border border-white/20 hover:border-white text-white px-4 py-2 text-sm flex items-center gap-2 transition-colors"
              data-testid="morning-refresh"
            >
              <RefreshCw size={12} />
              Opdater
            </button>
          </div>
        </div>

        {/* Service availability — biggest priority alert */}
        {availability && !availability.accepting_orders && (
          <div className="mt-4 bg-red-700 border-2 border-red-400 px-4 py-4 flex items-start gap-3 flex-wrap" data-testid="availability-closed-banner">
            <span className="text-3xl flex-shrink-0 leading-none">🚧</span>
            <div className="flex-1 min-w-[280px]">
              <div className="font-display font-bold text-lg text-white">SITE LUKKET FOR NYE BOOKINGER</div>
              <div className="text-sm text-red-100/90 mt-1">
                {availability.reason === "no_drivers"
                  ? `Ingen aktive chauffører — kunder kan ikke booke. Onboard mindst 1 chauffør for at åbne for bookinger igen.`
                  : availability.message || "Bookinger er midlertidigt lukket"}
              </div>
              <div className="text-xs text-red-200/70 mt-2">
                Aktive chauffører: <b>{availability.active_drivers}</b> · Online (sidste 5 min): <b>{availability.online_drivers}</b>
              </div>
            </div>
            <button
              onClick={() => setWizardOpen(true)}
              className="bg-white text-red-700 hover:bg-amber-300 hover:text-brand-dark px-4 py-2.5 text-sm font-bold flex items-center gap-2 transition-colors flex-shrink-0"
              data-testid="open-wizard-from-closed"
            >
              <Zap size={14} /> Åbn for kunder →
            </button>
          </div>
        )}
        {availability && availability.accepting_orders && availability.bypass_active && (
          <div className="mt-4 bg-amber-600 border-2 border-amber-300 px-4 py-3 flex items-start gap-3 flex-wrap" data-testid="availability-bypass-banner">
            <span className="text-2xl flex-shrink-0 leading-none">⚡</span>
            <div className="flex-1 min-w-[260px]">
              <div className="font-display font-bold text-base text-white">BYPASS AKTIV — Site åbent uden chauffør-tjek</div>
              <div className="text-xs text-amber-100/90 mt-1">
                Husk at slå bypass fra når rigtige chauffører er onboardet.
              </div>
            </div>
            <button
              onClick={() => setWizardOpen(true)}
              className="bg-white text-amber-800 hover:bg-amber-100 px-3 py-2 text-xs font-bold flex items-center gap-1 transition-colors flex-shrink-0"
              data-testid="open-wizard-from-bypass"
            >
              Administrér
            </button>
          </div>
        )}

        {/* Status banner */}
        <div className="mt-6 flex items-center gap-3">
          {allOk && warnCount === 0 ? (
            <div className="bg-emerald-500/20 border border-emerald-400/50 px-4 py-3 flex items-center gap-3 flex-1" data-testid="status-all-ok">
              <CheckCircle2 size={22} className="text-emerald-300 flex-shrink-0" />
              <div>
                <div className="font-display font-bold text-lg text-emerald-200">Alt er klart — god dag derude 🚀</div>
                <div className="text-xs text-emerald-100/80 mt-0.5">
                  {okCount}/{checks.length} checks grønne · ingen åbne action items
                </div>
              </div>
            </div>
          ) : allOk && warnCount > 0 ? (
            <div className="bg-amber-500/20 border border-amber-400/50 px-4 py-3 flex items-center gap-3 flex-1" data-testid="status-warning-only">
              <AlertTriangle size={22} className="text-amber-300 flex-shrink-0" />
              <div>
                <div className="font-display font-bold text-lg text-amber-200">
                  {warnCount} {warnCount === 1 ? "ting forfalder snart" : "ting forfalder snart"}
                </div>
                <div className="text-xs text-amber-100/80 mt-0.5">
                  {okCount}/{checks.length} checks grønne · gennemgå de gule kort
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-red-500/20 border border-red-400/50 px-4 py-3 flex items-center gap-3 flex-1" data-testid="status-needs-action">
              <AlertTriangle size={22} className="text-red-300 flex-shrink-0" />
              <div>
                <div className="font-display font-bold text-lg text-red-200">
                  {problemCount} {problemCount === 1 ? "ting kræver" : "ting kræver"} din opmærksomhed
                </div>
                <div className="text-xs text-red-100/80 mt-0.5">
                  {okCount}/{checks.length} checks grønne{warnCount > 0 ? ` · ${warnCount} gul` : ""} · scroll ned for handling
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Action banners */}
        {critical.length > 0 && (
          <div className="mt-3 space-y-1.5">
            {critical.map((a, i) => (
              <div key={i} className="bg-red-600/30 border border-red-400/60 text-red-100 px-3 py-2 text-sm flex items-center gap-2" data-testid={`morning-alert-critical-${i}`}>
                <AlertTriangle size={14} className="flex-shrink-0" />
                <span className="flex-1">{a.msg}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Checklist grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="morning-checklist">
        {checks.map((c) => (
          <CheckCard key={c.key} check={c} onJump={c.jumpTo ? () => onJumpToTab?.(c.jumpTo) : null} />
        ))}
      </div>

      {/* Quick actions */}
      <div className="bg-white border border-brand p-4 quick-actions-print-hide">
        <h3 className="font-display font-bold text-base text-brand-dark mb-3">Hurtig handling</h3>
        <div className="flex flex-wrap gap-2">
          <QuickBtn label="📝 Opret manuel ordre" onClick={() => setManualOpen(true)} testId="quick-manual-booking" />
          <QuickBtn label="🚪 Åbn for kunder" onClick={() => setWizardOpen(true)} testId="quick-open-for-business" />
          <QuickBtn label="Se dagens chauffører" onClick={() => onJumpToTab?.("drift")} />
          <QuickBtn label="Åbn tickets" onClick={() => onJumpToTab?.("tickets")} />
          <QuickBtn label="Drift-chat" onClick={() => onJumpToTab?.("driver_chat")} />
          <QuickBtn label="Mail-routing tjek" onClick={() => onJumpToTab?.("mail_routing")} />
          <QuickBtn label="Bookinger" onClick={() => onJumpToTab?.("bookings")} />
          <QuickBtn label="Kunder" onClick={() => onJumpToTab?.("customers")} />
        </div>
      </div>

      <div className="text-xs text-brand-text2 text-center">
        Auto-refresh hver 90. sekund · senest opdateret {new Date(data.generated_at).toLocaleTimeString("da-DK", { hour: "2-digit", minute: "2-digit" })}
      </div>

      <OpenForBusinessWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onChanged={(avail) => { setAvailability(avail); load(true); }}
      />

      <ManualBookingModal
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        onCreated={() => load(true)}
      />
    </div>
  );
}

function CheckCard({ check, onJump }) {
  const isWarn = check.ok && check.warn;
  return (
    <div
      className={`bg-white border p-3.5 transition-colors ${
        !check.ok ? "border-red-400" : isWarn ? "border-amber-400" : "border-brand"
      }`}
      data-testid={`check-${check.key}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 text-brand-text2 text-xs font-bold uppercase tracking-widest">
          {check.icon}
          {check.label}
        </div>
        {!check.ok ? (
          <AlertTriangle size={16} className="text-red-600 flex-shrink-0 animate-pulse" data-testid={`check-${check.key}-problem`} />
        ) : isWarn ? (
          <AlertTriangle size={16} className="text-amber-600 flex-shrink-0" data-testid={`check-${check.key}-warn`} />
        ) : (
          <CheckCircle2 size={16} className="text-emerald-600 flex-shrink-0" data-testid={`check-${check.key}-ok`} />
        )}
      </div>
      <div className="text-sm font-semibold text-brand-dark">{check.value}</div>
      {check.detail && (
        <div className="text-xs text-brand-text2 mt-1">{check.detail}</div>
      )}
      {check.problem && (
        <div className="text-xs text-red-700 font-bold mt-1 flex items-center gap-1">
          <AlertTriangle size={11} /> {check.problem}
        </div>
      )}
      {!check.problem && check.warning && (
        <div className="text-xs text-amber-700 font-bold mt-1 flex items-center gap-1" data-testid={`check-${check.key}-warning-text`}>
          <AlertTriangle size={11} /> {check.warning}
        </div>
      )}
      {onJump && (!check.ok || isWarn) && (
        <button
          onClick={onJump}
          className={`text-xs mt-2 flex items-center gap-1 font-bold ${
            isWarn && check.ok ? "text-amber-700 hover:text-amber-900" : "text-brand-red hover:text-red-700"
          }`}
          data-testid={`check-${check.key}-jump`}
        >
          {isWarn && check.ok ? "Gennemgå nu" : "Håndtér nu"} <ArrowRight size={11} />
        </button>
      )}
    </div>
  );
}

function QuickBtn({ label, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="text-xs border border-brand text-brand-dark hover:bg-brand-dark hover:text-white px-3 py-2 flex items-center gap-1 transition-colors"
    >
      {label} <ArrowRight size={11} />
    </button>
  );
}
